import {useDebounce} from '@logistics-frontend/hooks'
import useRefresh from '@logistics-frontend/hooks/use-refresh'
import {equals, filter, find, firstOf, flow, get, head, pipe} from '@logistics-frontend/utils'
import {useFormContext} from '@yandex-delivery/form'
import {isPoint} from '@yandex-taxi/react-integration-api'
import {useCallback, useEffect, useMemo, useRef, useState} from 'react'

import {OrderEventGoal, useFormUserEventHandler} from 'src/shared/hooks'
import type {OffersCalculateResponse, Order} from 'src/shared/types'

import {OfferDeliveryType} from './consts'
import {useFetchOffersCalculate} from './OfferList/use-fetch-offers-calculate'
import {OfferCalculate} from './schema'
import {fromOfferBody} from './utils/from-offer-body'
import {toOffers} from './utils/to-offers'

const setFieldValueOptions = {
  shouldValidate: false,
  shouldDirty: false,
  shouldTouch: false,
}

function useOffersBody() {
  const {watch} = useFormContext<Order>()
  const offerBody = fromOfferBody(watch())
  const hash = JSON.stringify(offerBody)

  return useMemo(() => {
    try {
      OfferCalculate.validateSync(offerBody)

      return {
        body: offerBody,
        updateKey: hash,
      }
    } catch (error) {
      return {}
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hash])
}

export function useOffers() {
  const prevUpdateKey = useRef<string>()
  const prevPaymentMethodId = useRef<string>()
  const [isPollingAvailable, setIsPollingAvailable] = useState(false)

  const {watch, resetField, setFieldValue, getFieldErrors} = useFormContext<Order>()
  const {fetchOffersCalculateHandler} = useFetchOffersCalculate()
  const {body: offersBody, updateKey} = useOffersBody()
  const handleReachGoal = useFormUserEventHandler()

  const sourcePoint = watch('sourcePoint')
  const destinationPoints = watch('destinationPoints')
  const selectedVerticalName = watch('offerFlow.vertical.name')
  const selectedTariffName = watch('offerFlow.offer.tariff')
  const verticalDueSettings = watch('offerFlow.vertical.dueSettings')
  const selectedDeliveryType = watch('offerFlow.deliveryType')
  const paymentMethodId = watch('paymentMethod.id')
  const isAddressesSelected = [sourcePoint, ...destinationPoints].map(get('address.coordinates')).every(isPoint)
  const isValid = getFieldErrors('offerFlow.offer') === undefined

  function handleDueSelect() {
    handleReachGoal(OrderEventGoal.WhenDueClick)
  }

  const successCalculateHandler = useCallback(
    (payload?: OffersCalculateResponse) => {
      const offers = toOffers(payload)
      const foundOffer = pipe(
        offers,
        firstOf(
          find(flow(get('tariff'), equals(selectedTariffName))),
          flow(filter(flow(get('vertical'), equals(selectedVerticalName))), filter(flow(get('id'), Boolean)), head),
        ),
      )

      setFieldValue('offerFlow.offer', foundOffer, {
        ...setFieldValueOptions,
        shouldValidate: true,
      })
      setFieldValue('__settings.__offers', offers, setFieldValueOptions)
    },
    [selectedTariffName, selectedVerticalName, setFieldValue],
  )

  const memoizedOffersCalculateHandler = useCallback(() => {
    if (offersBody === undefined) {
      return
    }

    fetchOffersCalculateHandler(offersBody, {
      onSuccess: successCalculateHandler,
    })
  }, [fetchOffersCalculateHandler, offersBody, successCalculateHandler])

  /**
   * FIXME: Нашел уже существующее решение в кодовой базе!!
   * @TODO: Победить костыль я не смог без глобального переписывания компонента.
   * ---
   * Нашел уже существующее решение в кодовой базе!
   * Поллинг должен быть только после первого успешного получения офферов.
   * Прекращаем поллинг когда offersBody невалидные
   * При рефакторинге этого компонента нужно избавиться от этого кода в решение более элегантное
   * */
  useRefresh(isPollingAvailable ? memoizedOffersCalculateHandler : undefined, {interval: 120000})

  /**
   * @TODO - При рефакторинге избавиться от всех useEffect, useDebounce etc.
   * Оффера должен стать отдельным компонентом, который будет перезапрашивать оффера при изменении key
   */
  useDebounce(
    () => {
      if (offersBody === undefined) {
        setFieldValue('offerFlow.offer', undefined, setFieldValueOptions)
        setFieldValue('__settings.__offers', undefined, setFieldValueOptions)
        setIsPollingAvailable(false)
        return
      }

      if (prevUpdateKey.current === updateKey && prevPaymentMethodId.current === paymentMethodId) {
        return
      }

      fetchOffersCalculateHandler(offersBody, {
        onSuccess: payload => {
          successCalculateHandler(payload)
          setIsPollingAvailable(true)
        },
      })

      prevUpdateKey.current = updateKey
      prevPaymentMethodId.current = paymentMethodId
    },
    300,
    [
      offersBody,
      updateKey,
      fetchOffersCalculateHandler,
      setFieldValue,
      paymentMethodId,
      selectedVerticalName,
      selectedTariffName,
      successCalculateHandler,
    ],
  )

  /**
   * @BusinessLogic:
   * Нужно сбрасывать время и дату, если выбран тип доставки "Сейчас"
   */
  useEffect(() => {
    if (selectedDeliveryType === OfferDeliveryType.Now) {
      resetField('offerFlow.deliveryTime')
      resetField('offerFlow.deliveryDate')
    }
  }, [selectedDeliveryType, resetField])

  return {
    isValid,
    isAddressesSelected,
    selectedVerticalName,
    selectedDeliveryType,
    verticalDueSettings,
    handleDueSelect,
  } as const
}
